import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { PaHttpClient } from 'src/app/clients/http-client';
import { parseActivityPointsReport } from 'src/app/shared/services/collab-analytics.service';
import {
  ActivityPointsRecap,
  ApiEnvelope,
  RecapPeriod,
  SuperAdminReport,
  parseActivityPointsRecap,
  unwrapEnvelope,
} from 'src/app/shared/services/super-admin-dashboard.service';

export type ApiPeriod = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all_time';
export type ApiFilter = 'engagement' | 'connection' | 'creation';

export interface ActivityPointsProfile {
  creation: number;
  engagement: number;
  connection: number;
  activity_points_level: string;
  referrer_token?: string;
  contacts?: number;
  referrals?: number;
}

export interface PeriodData {
  currentPoints: number;
  change_points: number;
  change_percent: number;
}

export interface ActivityPointsOverview {
  today: PeriodData;
  week: PeriodData;
  monthly: PeriodData;
  yearly: PeriodData;
}

export interface ReportItemData {
  count: number;
  points: number;
}

export interface ReportItem {
  current: ReportItemData;
  previous: ReportItemData;
  past: ReportItemData;
}

export type ActivityPointsReport = Record<string, ReportItem>;

@Injectable({ providedIn: 'root' })
export class ActivityPointsService {
  private readonly httpClient = inject(PaHttpClient);

  getProfile(userId?: string): Observable<ActivityPointsProfile> {
    return this.httpClient
      .getWithParams<{ success: boolean; message?: string; data: ActivityPointsProfile }>(
        'activity-points-profile',
        userId ? { user_id: userId } : {}
      )
      .pipe(
        map(res => {
          if (!res.success) throw new Error(res.message ?? 'Failed to load profile');
          return res.data;
        })
      );
  }

  getOverview(userId?: string): Observable<ActivityPointsOverview> {
    return this.httpClient
      .getWithParams<{ success: boolean; message?: string; data: ActivityPointsOverview }>(
        'activity-points-overview',
        userId ? { user_id: userId } : {}
      )
      .pipe(
        map(res => {
          if (!res.success) throw new Error(res.message ?? 'Failed to load overview');
          return res.data;
        })
      );
  }

  // Graph data points + total for the current user (activity-points)
  getGraphReport(period: ApiPeriod, type: ApiFilter, userId?: string): Observable<SuperAdminReport> {
    return this.httpClient
      .getWithParams<ApiEnvelope<Record<string, unknown>> | Record<string, unknown>>('activity-points', { period, type, filter: type, ...(userId ? { user_id: userId } : {}) })
      .pipe(map(res => parseActivityPointsReport(unwrapEnvelope(res, 'Failed to load report') ?? {})));
  }

  // Period recap (activity-points-overview with a period): per-metric points + change percent
  getRecap(period: RecapPeriod, type?: ApiFilter | null, userId?: string): Observable<ActivityPointsRecap> {
    return this.httpClient
      .getWithParams<ApiEnvelope<Record<string, unknown>> | Record<string, unknown>>('activity-points-overview', {
        period,
        ...(type ? { type } : {}),
        ...(userId ? { user_id: userId } : {}),
      })
      .pipe(map(res => parseActivityPointsRecap(unwrapEnvelope(res, 'Failed to load recap') ?? {})));
  }

  getReport(
    period: ApiPeriod,
    filter: ApiFilter,
    page?: number,
    limit?: number
  ): Observable<ActivityPointsReport> {
    const params: Record<string, string> = { period, filter };
    if (page !== undefined) params['page'] = String(page);
    if (limit !== undefined) params['limit'] = String(limit);

    return this.httpClient
      .getWithParams<{ success: boolean; message?: string; data: Record<string, unknown> }>(
        'activity-points',
        params
      )
      .pipe(
        map(res => {
          if (!res.success) throw new Error(res.message ?? 'Failed to load report');
          // Strip metadata scalar fields (e.g. `period`, `page`, `limit`) — keep only report objects
          const report: ActivityPointsReport = {};
          for (const [key, value] of Object.entries(res.data)) {
            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
              report[key] = value as ReportItem;
            }
          }
          return report;
        })
      );
  }
}
